// src/services/api.ts
import axios from 'axios';

// ======================== BASE URL ========================
// const API_BASE_URL = 'http://localhost:8000/api/v1/client';
const API_BASE_URL = 'https://server.licenseapp.ir/api/v1/client';

// ======================== AXIOS INSTANCE ========================
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

api.interceptors.response.use(
    (res) => res,
    (err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('client_data');
            if (window.location.pathname !== '/login') window.location.replace('/login');
            return Promise.reject(new Error('نشست شما منقضی شده است.'));
        }
        const msg = err?.response?.data?.message || err?.message || 'خطای ناشناخته';
        return Promise.reject(new Error(msg));
    }
);

// ======================== HELPERS ========================
const handleResponse = (promise) => promise.then((res) => res.data);
export const toSafeNumber = (value, fallback = 0) => {
    const num = Number(value);
    return isNaN(num) ? fallback : num;
};

// ======================== TYPES (خلاصه) ========================
// ... (همان typeهایی که قبلاً داشتید، برای اختصار حذف شدند، ولی در فایل واقعی باید باشند)

// ======================== API ========================
export const authAPI = {
    requestOTP: (phone) => handleResponse(api.post('/auth/login', { phone_number: phone })),
    verifyOTP: (phone, code) => handleResponse(api.post('/auth/verify', { phone_number: phone, code })),
    /**
     * درخواست نمایندگی.
     *
     * این یکی عمداً مسیر مطلق دارد: ثبت درخواست نمایندگی روی `/agents/request`
     * است نه زیر `/client`. قبلاً `/request` نسبی بود که با baseURL می‌شد
     * `/client/request` — مسیری که وجود ندارد و فرم بی‌صدا ۴۰۴ می‌گرفت.
     */
    requestAgency: (formData) =>
        handleResponse(
            api.post('/api/v1/agents/request', formData, {
                baseURL: API_BASE_URL.replace(/\/api\/v1\/client$/, ''),
                headers: { 'Content-Type': 'multipart/form-data' },
            })
        ),
};

export const profileAPI = {
    getProfile: () => handleResponse(api.get('/profile')),
    confirmProfile: (payload) => handleResponse(api.post('/profile/confirm', payload)),
};

export const meAPI = {
    getMe: () => handleResponse(api.get('/me')),
};

export const dashboardAPI = {
    getDashboard: (period = 'all') => handleResponse(api.get(`/dashboard?period=${period}`)),
};

export const productsAPI = {
    getClientProducts: () => handleResponse(api.get('/products')).then((data) => data?.items || data || []),
};

export const ordersAPI = {
    createOrder: (payload) => {
        const product_id = toSafeNumber(payload.product_id, 0);
        const quantity = Math.max(1, toSafeNumber(payload.quantity, 1));
        if (product_id <= 0) return Promise.reject(new Error('شناسه محصول نامعتبر است.'));
        return handleResponse(api.post('/orders', { product_id, quantity }));
    },
    cancelOrder: (orderId) => {
        const safeId = toSafeNumber(orderId, 0);
        if (safeId <= 0) return Promise.reject(new Error('شناسه سفارش نامعتبر است.'));
        return handleResponse(api.post(`/orders/${safeId}/cancel`));
    },
    listOrders: (page = 1, limit = 20) =>
        handleResponse(api.get('/orders', { params: { page: toSafeNumber(page, 1), limit: toSafeNumber(limit, 20) } })),
    getAllOrders: () => ordersAPI.listOrders(1, 9999), // سازگاری با کدهای قدیمی
    getOrderById: (orderId) => {
        const safeId = toSafeNumber(orderId, 0);
        if (safeId <= 0) return Promise.reject(new Error('شناسه سفارش نامعتبر است.'));
        return handleResponse(api.get(`/orders/${safeId}`));
    },
};

export const invoicesAPI = {
    listInvoices: (page = 1, limit = 20) =>
        handleResponse(api.get('/invoices', { params: { page: toSafeNumber(page, 1), limit: toSafeNumber(limit, 20) } })),
    getInvoiceById: (invoiceId) => {
        const safeId = toSafeNumber(invoiceId, 0);

        if (safeId <= 0) return Promise.reject(new Error('شناسه فاکتور نامعتبر است.'));
        return handleResponse(api.get(`/invoices/${safeId}`));
    },
};

export const paymentsAPI = {
    createPayment: (invoiceId) => {
        const safeId = toSafeNumber(invoiceId?.invoice_id, 0);
        console.log(safeId,invoiceId)
        if (safeId <= 0) return Promise.reject(new Error('شناسه فاکتور نامعتبر است.'));
        return handleResponse(api.post('/payments/create', { invoice_id: safeId }));
    },
    verifyPayment: (authority, status = 'OK') =>
        handleResponse(api.get('/payments/verify', { params: { Authority: authority, Status: status } })),
};

export const walletAPI = {
    getWalletSummary: () => handleResponse(api.get('/wallet')),
    listTransactions: (page = 1, limit = 20) =>
        handleResponse(api.get('/transactions', { params: { page: toSafeNumber(page, 1), limit: toSafeNumber(limit, 20) } })),
    chargeWallet: (amount) => {
        const safeAmount = toSafeNumber(amount, 0);
        if (safeAmount <= 0) return Promise.reject(new Error('مبلغ شارژ نامعتبر است.'));
        return handleResponse(api.post('/wallet/charge', { amount: safeAmount }));
    },
};

export const licensesAPI = {
    getLicenses: () => handleResponse(api.get('/licenses')),
    getMyLicenses: () => handleResponse(api.get('/licenses')),
    requestCollect: (licenseId, note) => {
        const safeId = toSafeNumber(licenseId, 0);
        if (safeId <= 0) return Promise.reject(new Error('شناسه لایسنس نامعتبر است.'));
        return handleResponse(api.post(`/licenses/${safeId}/collect-request`, { note }));
    },
    listCollectRequests: () =>
        handleResponse(api.get('/licenses/collect-requests')).catch(() => ({ items: [] })), // در صورت خطا، لیست خالی برگردان
    checkExpirations: () => handleResponse(api.get('/licenses/expirations')),
    createRenewalInvoice: (licenseId) => {
        const safeId = toSafeNumber(licenseId, 0);
        if (safeId <= 0) return Promise.reject(new Error('شناسه لایسنس نامعتبر است.'));
        return handleResponse(api.post('/licenses/renew', { license_id: safeId }));
    },
};

export const customersAPI = {
    listCustomers: (params) => handleResponse(api.get('/customers', { params })),
    checkBuyer: (phone) => handleResponse(api.post('/customers/check', { phone })),
    addCustomer: (payload) => handleResponse(api.post('/customers', payload)),
};

export const notificationsAPI = {
    listNotifications: (params) => handleResponse(api.get('/notifications', { params })),
    markAsRead: (ids) => {
        const safeIds = Array.isArray(ids) ? ids.map(id => toSafeNumber(id, 0)).filter(id => id > 0) : [];
        return handleResponse(api.post('/notifications/read', { ids: safeIds }));
    },
    markGroupAsRead: (group) => handleResponse(api.post('/notifications/read-group', { group })),
    getUnreadSummary: () => handleResponse(api.get('/notifications/unread-summary')),
    listUnread: () => handleResponse(api.get('/notifications/unread')),
    list: (params) => notificationsAPI.listNotifications(params), // alias
};

// ======================== EXPORT‌های سازگار با کامپوننت‌ها ========================
export const clientProfileAPI = profileAPI;
export const clientLicensesAPI = {
    getLicenses: licensesAPI.getLicenses,
    requestCollect: licensesAPI.requestCollect,
    getCollectRequests: licensesAPI.listCollectRequests,
    checkExpirations: licensesAPI.checkExpirations,
    createRenewalInvoice: licensesAPI.createRenewalInvoice,
};
export const clientInvoicesAPI = {
    list: invoicesAPI.listInvoices,
    getById: invoicesAPI.getInvoiceById,
};
export const clientNotificationsAPI = {
    list: notificationsAPI.list,
    listNotifications: notificationsAPI.listNotifications,
    markAsRead: notificationsAPI.markAsRead,
    markGroupAsRead: notificationsAPI.markGroupAsRead,
    getUnreadSummary: notificationsAPI.getUnreadSummary,
    listUnread: notificationsAPI.listUnread,
    markAllAsRead: () => notificationsAPI.markAsRead([]),
    getUnread: notificationsAPI.listUnread,
};
export const clientPaymentsAPI = {
    create: paymentsAPI.createPayment,
    verify: paymentsAPI.verifyPayment,
};
export const clientProductsAPI = productsAPI;

// ============================================================
//   SUPPORT (تیکت‌ها)
// ============================================================

export type TicketStatus = 'open' | 'pending' | 'answered' | 'closed';
export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';
export type TicketCategory = 'technical' | 'financial' | 'license' | 'account' | 'other';

export interface TicketMessage {
    id: number;
    body: string;
    sender_type: 'user' | 'admin' | 'system';
    /** برای پیام ادمین همیشه «تیم پشتیبانی» است، نه نام واقعی */
    display_name: string;
    created_at: string;
}

export interface Ticket {
    id: number;
    ticket_no: string;
    subject: string;
    status: TicketStatus;
    status_label: string;
    priority: TicketPriority;
    priority_label: string;
    category: TicketCategory;
    category_label: string;
    message_count: number;
    /** true یعنی توپ در زمین پشتیبانی است */
    awaiting_reply: boolean;
    last_reply_at: string | null;
    created_at: string;
    closed_at: string | null;
    messages?: TicketMessage[];
}

export const supportAPI = {
    list: (params?: { page?: number; pageSize?: number; status?: TicketStatus; search?: string }) =>
        api.get('/support', { params }).then((r) => r.data as {
            tickets: Ticket[];
            pagination: { currentPage: number; pageSize: number; total: number; totalPages: number };
        }),

    stats: () => api.get('/support/stats').then((r) => r.data.stats as Record<string, number>),

    /** گفتگو — یادداشت‌های داخلی پشتیبانی در این خروجی نیستند */
    getById: (id: number) => api.get(`/support/${id}`).then((r) => r.data.ticket as Ticket),

    create: (payload: {
        subject: string;
        body: string;
        category?: TicketCategory;
        priority?: TicketPriority;
    }) => api.post('/support', payload).then((r) => r.data.ticket as Ticket),

    /** پاسخ کاربر — تیکت را دوباره به وضعیت «باز» برمی‌گرداند */
    reply: (id: number, body: string) =>
        api.post(`/support/${id}/reply`, { body }).then((r) => r.data.ticket as Ticket),

    close: (id: number) => api.post(`/support/${id}/close`).then((r) => r.data.ticket as Ticket),
};

export default api;
